import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  Image,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  useToast
} from "@chakra-ui/react";
import {
  FaBuilding,
  FaCalendarAlt,
  FaCity,
  FaEnvelope,
  FaGlobe,
  FaIdBadge,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaPhone,
  FaShieldAlt,
  FaSyncAlt,
  FaUserTie
} from "react-icons/fa";
import { api } from "../api";
import { useBranding } from "../context/BrandingContext";

const parseLocalUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch (_error) {
    return {};
  }
};

const notInformed = "Não informado";

const formatDate = (value) => {
  if (!value) return notInformed;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return notInformed;
  return date.toLocaleDateString("pt-BR");
};

const addMonths = (value, months) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const next = new Date(date);
  next.setMonth(next.getMonth() + Number(months || 0));
  return next;
};

const InfoRow = ({ icon, label, value }) => (
  <HStack align="start" spacing={2.5} className="company-info-row">
    <Box className="company-info-icon">{icon}</Box>
    <Box minW={0}>
      <Text className="company-info-label">{label}</Text>
      <Text className="company-info-value" noOfLines={3}>
        {value || notInformed}
      </Text>
    </Box>
  </HStack>
);

const LoadingCard = () => (
  <Box className="company-info-card">
    <Stack spacing={3}>
      <Skeleton height="24px" borderRadius="12px" />
      <Skeleton height="16px" borderRadius="10px" />
      <Skeleton height="16px" borderRadius="10px" />
      <Skeleton height="16px" borderRadius="10px" />
      <Skeleton height="16px" borderRadius="10px" />
    </Stack>
  </Box>
);

const InformacoesEmpresa = () => {
  const [loading, setLoading] = useState(true);
  const [usuario, setUsuario] = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [plano, setPlano] = useState(null);
  const toast = useToast();

  const localUser = useMemo(() => parseLocalUser(), []);
  const { branding, assetUrl } = useBranding();

  const loadInfo = useCallback(async () => {
    setLoading(true);
    try {
      const meRes = await api.get("/v2/usuarios/me");
      const me = meRes.data || {};
      setUsuario(me);

      const empresaId = Number(me?.empresa_id || localUser?.empresa_id || 0);
      if (empresaId) {
        const empresaRes = await api.get(`/v2/empresas/${empresaId}`);
        setEmpresa(empresaRes.data || null);
      } else {
        setEmpresa(null);
      }

      if (me?.plano_id) {
        const planosRes = await api.get("/v2/planos", { params: { page: 1, page_size: 200 } });
        const planos = planosRes.data?.items || [];
        const planoAtual = planos.find((item) => Number(item.id) === Number(me.plano_id)) || null;
        setPlano(planoAtual);
      } else {
        setPlano(null);
      }
    } catch (_error) {
      toast({
        title: "Erro ao carregar informações da empresa",
        status: "error",
        duration: 3000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  }, [localUser?.empresa_id, toast]);

  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  const vencimento = useMemo(() => {
    if (usuario?.data_fim_assinatura) return formatDate(usuario.data_fim_assinatura);
    if (usuario?.data_fim) return formatDate(usuario.data_fim);
    if (usuario?.data_ativacao && usuario?.duracao_plano) {
      const next = addMonths(usuario.data_ativacao, usuario.duracao_plano);
      return formatDate(next);
    }
    return notInformed;
  }, [usuario?.data_ativacao, usuario?.data_fim, usuario?.data_fim_assinatura, usuario?.duracao_plano]);

  const cidadeEstado = useMemo(() => {
    const cidade = empresa?.cidade || usuario?.cidade || "";
    const estado = empresa?.estado || "";
    if (!cidade && !estado) return notInformed;
    return [cidade, estado].filter(Boolean).join(" - ");
  }, [empresa?.cidade, empresa?.estado, usuario?.cidade]);

  const enderecoCompleto = useMemo(() => {
    const partes = [empresa?.endereco, empresa?.bairro, empresa?.cidade, empresa?.estado].filter(Boolean);
    return partes.length ? partes.join(", ") : notInformed;
  }, [empresa?.bairro, empresa?.cidade, empresa?.endereco, empresa?.estado]);

  const logoUrl = assetUrl(branding.logo_url) || "/sistema/logo.png";

  return (
    <Box px={{ base: 3, md: 5 }} py={{ base: 3, md: 4 }} className="company-page">
      <Box className="company-shell">
        <Stack spacing={4}>
          <HStack justify="space-between" align="center" flexWrap="wrap" spacing={2}>
            <HStack spacing={3}>
              <Box className="company-brand-avatar">
                <Image src={logoUrl} alt={branding.display_name || "EU Monitoro"} boxSize="34px" objectFit="contain" />
              </Box>
              <Stack spacing={0}>
                <Text className="company-title">Informações da empresa</Text>
                <Text className="company-subtitle">Dados da empresa responsável pelo seu monitoramento</Text>
              </Stack>
            </HStack>
            <Button
              leftIcon={<FaSyncAlt />}
              size="sm"
              variant="outline"
              className="company-refresh-btn"
              isLoading={loading}
              onClick={loadInfo}
            >
              Atualizar
            </Button>
          </HStack>

          <Box className="company-about-card">
            <HStack spacing={2} align="center" mb={2}>
              <Box color="#ccfff4">
                <FaInfoCircle />
              </Box>
              <Text className="company-section-title">Sobre o {branding.display_name || "EU Monitoro"}</Text>
            </HStack>
            <Text className="company-about-text">
              Plataforma de monitoramento com câmeras, alertas, mapa em tempo real e apoio operacional para resposta rápida.
            </Text>
            <HStack spacing={2} mt={2} flexWrap="wrap">
              <Badge className="company-chip">
                <FaShieldAlt style={{ marginRight: 6 }} />
                Monitoramento inteligente
              </Badge>
              <Badge className="company-chip">
                <FaBuilding style={{ marginRight: 6 }} />
                Segurança comunitária
              </Badge>
              <Badge className="company-chip">
                <FaSyncAlt style={{ marginRight: 6 }} />
                Operação em tempo real
              </Badge>
            </HStack>
          </Box>

          {loading ? (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <LoadingCard />
              <LoadingCard />
            </SimpleGrid>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <Box className="company-info-card">
                <Text className="company-section-title" mb={2}>
                  Empresa contratada
                </Text>
                <Stack spacing={2.5}>
                  <InfoRow icon={<FaBuilding />} label="Nome da empresa" value={empresa?.nome} />
                  <InfoRow icon={<FaIdBadge />} label="Slug" value={empresa?.slug} />
                  <InfoRow icon={<FaShieldAlt />} label="Status" value={empresa?.status || "Ativo"} />
                  <InfoRow icon={<FaCity />} label="Cidade / Estado" value={cidadeEstado} />
                  <InfoRow icon={<FaMapMarkerAlt />} label="Endereço" value={enderecoCompleto} />
                  <InfoRow icon={<FaEnvelope />} label="E-mail da empresa" value={empresa?.email} />
                  <InfoRow icon={<FaPhone />} label="Telefone" value={empresa?.telefone} />
                  <InfoRow icon={<FaGlobe />} label="Site" value={empresa?.site} />
                  <InfoRow icon={<FaCalendarAlt />} label="Cadastro da empresa" value={formatDate(empresa?.created_at)} />
                </Stack>
              </Box>

              <Box className="company-info-card">
                <Text className="company-section-title" mb={2}>
                  Seu vínculo de assinatura
                </Text>
                <Stack spacing={2.5}>
                  <InfoRow icon={<FaUserTie />} label="Cliente" value={usuario?.nome || localUser?.nome} />
                  <InfoRow icon={<FaEnvelope />} label="E-mail do cliente" value={usuario?.email || localUser?.email} />
                  <InfoRow icon={<FaShieldAlt />} label="Plano atual" value={plano?.nome || usuario?.plano || notInformed} />
                  <InfoRow icon={<FaCalendarAlt />} label="Vencimento" value={vencimento} />
                  <Divider borderColor="rgba(191, 254, 232, 0.24)" />
                  <Text className="company-note">
                    Se algum dado da empresa estiver incorreto, ajuste no painel administrativo em Configurações.
                  </Text>
                </Stack>
              </Box>
            </SimpleGrid>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default InformacoesEmpresa;
